import React, { useContext, useState } from 'react';
import { useMutation } from '@apollo/client';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import Link from 'next/link';
import FormSubmitSuccess from 'components/Form/FormSubmitSuccess';
import InputField from 'components/Form/InputField';
import SubmitButton from 'components/Form/SubmitButton';
import { CUSTOMER_LOGIN_MUTATION, customerLoginVars } from 'helpers/customer';
import FormSubmitErrors from 'components/Form/FormSubmitErrors';
import { setCustomerToken as setCustomerTokenCookie } from 'helpers/cookies';
import PropTypes from 'prop-types';
import breakPoints from 'helpers/Styles';
import RequestFrom from '../ResetPassword/RequestFrom';

const Login = ({ handleLogin }) => {
  const [customerLogin, { error, data }] = useMutation(CUSTOMER_LOGIN_MUTATION);

  const [showResetPasswordRequestFrom, setShowResetPasswordRequestFrom] =
    useState(0);
  const [resetFormSent, setResetFormSent] = useState(0);

  // if (data && data.customerAccessTokenCreate) {
  //   const { customerAccessToken } = data.customerAccessTokenCreate;
  //   if (customerAccessToken && customerAccessToken.accessToken) {
  //     const { accessToken, expiresAt } = customerAccessToken;
  //     setCustomerTokenCookie({ accessToken, expiresAt });
  //   }
  // }

  const resetPasswordEmailSent = () => {
    setShowResetPasswordRequestFrom(false);
    setResetFormSent(true);
  };

  const signUpLink = '/me/sign-up';

  return (
    <>
      {showResetPasswordRequestFrom ? (
        <RequestFrom
          onCancle={() => setShowResetPasswordRequestFrom(false)}
          onSuccess={() => resetPasswordEmailSent()}
        />
      ) : (
        <div className="loginContainer">
          <h1 className="heading_style__hero">Log in</h1>
          <p className="login_description">
            Log in to your account to view your details and keep track of your
            orders.
          </p>
          <div className="login_form_container">
            <Formik
              initialValues={{ email: '', password: '' }}
              validationSchema={Yup.object({
                password: Yup.string()
                  .min(5, 'Must be 5 characters or more')
                  .required('Required'),
                email: Yup.string()
                  .email('Invalid email address')
                  .required('Required'),
              })}
              onSubmit={(values, { setSubmitting }) => {
                setResetFormSent(0);
                customerLogin({
                  variables: customerLoginVars(values),
                  update(proxy, { data: { customerAccessTokenCreate } }) {
                    const { customerAccessToken } = customerAccessTokenCreate;
                    if (
                      customerAccessToken &&
                      customerAccessToken.accessToken
                    ) {
                      const { accessToken, expiresAt } = customerAccessToken;
                      //setCustomerTokenCookie({ accessToken, expiresAt });
                      // redirect to /me
                      handleLogin({ accessToken, expiresAt });
                    }
                    setSubmitting(false);
                  },
                }).catch(() => {
                  setSubmitting(false);
                });
              }}
            >
              {({ isSubmitting }) => (
                <>
                  {data && data.customerAccessTokenCreate.customerUserErrors ? (
                    <FormSubmitErrors
                      errors={data.customerAccessTokenCreate.customerUserErrors}
                    />
                  ) : null}
                  {!resetFormSent && error ? (
                    <FormSubmitErrors
                      errors={[
                        { message: 'Something went wrong. Please try again.' },
                        { message: 'Something went wrong. Please try again.' },
                      ]}
                    />
                  ) : null}
                  {resetFormSent ? (
                    <FormSubmitSuccess
                      messages={[
                        {
                          message:
                            'We have sent you an email with a link to update your password.',
                        },
                      ]}
                    />
                  ) : null}
                  <Form>
                    <InputField
                      type="email"
                      name="email"
                      placeholder="Email Address"
                      autoComplete="username"
                      autoFocus
                    />
                    <InputField
                      type="password"
                      name="password"
                      placeholder="Password"
                      autoComplete="current-password"
                    />

                    <SubmitButton
                      isSubmitting={isSubmitting}
                      label="Login"
                      submittingLabel="Logging In..."
                    />

                    <a
                      type="button"
                      className="forgot-password-link"
                      onClick={setShowResetPasswordRequestFrom}
                    >
                      Forgot password?
                    </a>

                    <Link href="/me/sign-up">
                      <a
                        href={signUpLink}
                        alt="sign up"
                        className="extra-action-signUp"
                      >
                        SIGN UP
                      </a>
                    </Link>
                  </Form>
                </>
              )}
            </Formik>
          </div>
          <style jsx>
            {`
              .login_form_container {
                max-width: 416px;
                margin: 0 auto;
                margin-top: 48px;
              }
              .loginContainer {
                max-width: 640px;
                margin: 0 auto;
                text-align: center;
                padding-top: 50px;
                padding-bottom: 100px;
              }
              .login_description {
                font-family: 'Roboto-Medium';
                font-size: 16px;
                font-weight: 400;
                line-height: 24px;
                letter-spacing: 0em;
                text-align: center;

                margin-top: 24px;
              }
              .extra-action-signUp {
                font-family: 'Roboto-Medium';
                font-size: 16px;
                font-weight: 400;
                line-height: 24px;
                letter-spacing: 0em;
                text-align: center;

                margin-top: 16px;
              }
              .extra-action-signUp {
                display: block;
                margin-top: 30px;
                text-decoration: underline;
              }
              .extra-action-signUp:hover {
                color: #fd0300;
              }
              .extra-action {
                display: block;
                margin-top: 30px;
                color: rgb(80, 80, 80);
                text-align: center;
              }
              .success {
                color: rgb(79, 138, 16);
              }
              .heading_style__hero {
                color: #e4e4e4;
                font-family: 'big_noodle_titling';
                font-size: 39px;
                letter-spacing: 0;
                line-height: 48px;
                text-align: center;
              }
              .action_buttons {
                width: 100%;
                display: flex;
                justify-content: space-between;
              }
              .forgot-password-link {
                color: #e4e4e4;
                cursor: pointer;
                margin-top: 24px;
                display: block;
                font-family: 'Roboto-Medium';
                font-size: 13px;
                font-weight: 400;
                line-height: 20px;
                letter-spacing: 0em;
                text-align: center;
              }
              .forgot-password-link:hover {
                color: #fd0300;
              }
              @media (max-width: ${breakPoints.small}) {
                .loginContainer {
                  max-width: 340px;
                }
              }
            `}
          </style>
        </div>
      )}
    </>
  );
};

Login.propTypes = {
  handleLogin: PropTypes.func.isRequired,
};

export default Login;
